import * as React from 'react';
import {
  ReferenceInput,
  SelectInput,
  AutocompleteInput,
  useGetMany,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { EditableImage } from './EditableImageField';

const SOURCE = 'specialEvents';

const HERO_EVENT_FILTER = {
  status: 'notPast',
  isActive: 'true',
  hasSessions: 'false',
};

const HERO_GROUP_EVENT_FILTER = {
  status: 'notPast',
  isActive: 'true',
};

/** Web hero kart olcusu (275×344) — crop bu oranda. */
const HERO_CARD_WIDTH = 275;
const HERO_CARD_HEIGHT = 344;
const HERO_CARD_ASPECT = HERO_CARD_WIDTH / HERO_CARD_HEIGHT;

const PANEL_SX = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  bgcolor: '#fff',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 760,
  mt: 2,
};

const PANEL_HEADER_SX = {
  px: 2,
  py: 1.5,
  borderBottom: '1px solid #eef0f3',
  bgcolor: '#fafbfc',
};

const CARD_SX = {
  border: '1px solid #e8eaee',
  borderRadius: '10px',
  bgcolor: '#fff',
  mb: 1.5,
  overflow: 'hidden',
};

const HeroSpecialEvents = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const watched = useWatch({ name: SOURCE });
  const items = React.useMemo(() => (Array.isArray(watched) ? watched : []), [watched]);

  const [expanded, setExpanded] = React.useState(() => new Set());
  const [dragIndex, setDragIndex] = React.useState(null);
  const [overIndex, setOverIndex] = React.useState(null);
  /** Kart yalnızca tutamaçtan tutulunca sürüklenir; input içinde metin seçimi bozulmasın. */
  const [armedIndex, setArmedIndex] = React.useState(null);

  const eventIds = React.useMemo(
    () => [...new Set(items.map((i) => i?.eventId).filter(Boolean))],
    [items],
  );
  const groupIds = React.useMemo(
    () => [
      ...new Set(
        items
          .filter((i) => !i?.eventId)
          .map((i) => i?.groupid)
          .filter(Boolean),
      ),
    ],
    [items],
  );
  const { data: events = [] } = useGetMany(
    'events-v2',
    { ids: eventIds },
    { enabled: eventIds.length > 0 },
  );
  const { data: groups = [] } = useGetMany(
    'groupEvents',
    { ids: groupIds },
    { enabled: groupIds.length > 0 },
  );

  const titleFor = (item) => {
    if (item?.eventId) {
      const ev = events.find((e) => e?.id === item.eventId);
      if (!ev) return item.eventId;
      return ev.startdate ? `${ev.name} - ${moment(ev.startdate).format('DD-MM-YYYY')}` : ev.name;
    }
    if (item?.groupid) {
      const g = groups.find((e) => e?.id === item.groupid);
      return g?.name || item.groupid;
    }
    return '';
  };

  const writeList = (list) => setValue(SOURCE, list, { shouldDirty: true, shouldTouch: true });

  const toggle = (index) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const handleAdd = (event) => {
    event.preventDefault();
    const list = [...(getValues(SOURCE) || []), { eventId: null, groupid: null }];
    writeList(list);
    setExpanded((prev) => new Set(prev).add(list.length - 1));
  };

  const handleRemove = (index) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const list = [...(getValues(SOURCE) || [])];
    list.splice(index, 1);
    writeList(list);
    setExpanded(new Set());
  };

  const resetDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
    setArmedIndex(null);
  };

  const handleDrop = (target) => (event) => {
    event.preventDefault();
    if (dragIndex == null || dragIndex === target) {
      resetDrag();
      return;
    }
    const list = [...(getValues(SOURCE) || [])];
    const [moved] = list.splice(dragIndex, 1);
    list.splice(target, 0, moved);
    writeList(list);
    setExpanded(new Set());
    resetDrag();
  };

  return (
    <Box sx={PANEL_SX}>
      <Box sx={PANEL_HEADER_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ m: 0, color: '#111827' }}>
          {translate('heros.special_events', { _: 'Hero Etkinlikleri' })}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          {translate('heros.special_events_hint', {
            _: 'Ana sayfa üst carousel. Sürükleyip bırakarak sırayı ayarlayın, karta tıklayarak açıp kapatın.',
          })}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {items.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              border: '1px dashed #d1d5db',
              borderRadius: '10px',
              bgcolor: '#f9fafb',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {translate('heros.no_events', { _: 'Henüz etkinlik eklenmedi.' })}
            </Typography>
          </Box>
        ) : (
          items.map((item, index) => {
            const getSource = (field) => `${SOURCE}.${index}.${field}`;
            const isOpen = expanded.has(index);
            const isDropTarget = dragIndex != null && overIndex === index && dragIndex !== index;
            const title = titleFor(item);
            const featuredSource = getSource('featuredImage');
            const featuredRecord = item?.featuredImage
              ? { [featuredSource]: item.featuredImage }
              : undefined;

            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key -- satırların kalıcı id'si yok
                key={index}
                draggable={armedIndex === index}
                onDragStart={(event) => {
                  if (armedIndex !== index) {
                    event.preventDefault();
                    return;
                  }
                  event.dataTransfer.effectAllowed = 'move';
                  setDragIndex(index);
                }}
                onDragOver={(event) => {
                  if (dragIndex == null) return;
                  event.preventDefault();
                  if (overIndex !== index) setOverIndex(index);
                }}
                onDrop={handleDrop(index)}
                onDragEnd={resetDrag}
                sx={{
                  ...CARD_SX,
                  opacity: dragIndex === index ? 0.45 : 1,
                  borderColor: isDropTarget ? 'primary.main' : '#e8eaee',
                  boxShadow: isDropTarget ? '0 0 0 2px rgba(174,37,109,0.18)' : 'none',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={() => toggle(index)}
                  sx={{
                    px: 1.25,
                    py: 1,
                    cursor: 'pointer',
                    userSelect: 'none',
                    bgcolor: isOpen ? '#fafbfc' : '#fff',
                    borderBottom: isOpen ? '1px solid #eef0f3' : 'none',
                  }}
                >
                  <Tooltip title={translate('form.drag_to_reorder', { _: 'Sürükleyerek sırala' })}>
                    <Box
                      component="span"
                      onMouseDown={() => setArmedIndex(index)}
                      onMouseUp={() => setArmedIndex(null)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: 'inline-flex',
                        color: '#9ca3af',
                        cursor: 'grab',
                        '&:hover': { color: '#4b5563' },
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: '6px',
                      bgcolor: '#f3f4f6',
                      color: '#4b5563',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{ flex: 1, minWidth: 0, color: title ? '#111827' : '#9ca3af' }}
                  >
                    {title || translate('heros.pick_event', { _: 'Etkinlik seçilmedi' })}
                  </Typography>
                  {item?.featuredImage ? (
                    <Typography variant="caption" sx={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {translate('heros.has_custom_image', { _: 'Özel görsel' })}
                    </Typography>
                  ) : null}
                  <Tooltip title={translate('ra.action.delete', { _: 'Sil' })}>
                    <IconButton
                      type="button"
                      size="small"
                      onClick={handleRemove(index)}
                      aria-label="remove"
                      sx={{
                        color: '#9ca3af',
                        '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.06)' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      color: '#6b7280',
                      transition: 'transform 0.15s',
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </Stack>

                <Collapse in={isOpen} timeout={150} unmountOnExit>
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <ReferenceInput
                      source={getSource('eventId')}
                      reference="events-v2"
                      filter={HERO_EVENT_FILTER}
                      label="Etkinlik"
                      sort={{ field: 'startdate', order: 'ASC' }}
                      perPage={50}
                    >
                      <AutocompleteInput
                        label="Etkinlik"
                        optionText={(r) =>
                          `${r.name} - ${moment(r.startdate).format('DD-MM-YYYY')}`
                        }
                        matchSuggestion={() => true}
                        filterToQuery={(searchText) => ({ q: searchText })}
                        fullWidth
                      />
                    </ReferenceInput>
                    {!item?.eventId ? (
                      <ReferenceInput
                        source={getSource('groupid')}
                        reference="groupEvents"
                        filter={HERO_GROUP_EVENT_FILTER}
                        label="Grup etkinliği"
                        perPage={50}
                      >
                        <SelectInput label="Grup etkinliği" optionText="name" fullWidth />
                      </ReferenceInput>
                    ) : null}
                    <EditableImage
                      source={featuredSource}
                      record={featuredRecord}
                      aspect={HERO_CARD_ASPECT}
                      width={HERO_CARD_WIDTH}
                      height={HERO_CARD_HEIGHT}
                      label={translate('heros.featured_image', {
                        _: "Özel görsel (opsiyonel — yoksa etkinlik banner'ı)",
                      })}
                      hint={translate('heros.featured_crop_hint', {
                        _: `Hero kart ölçüsü ${HERO_CARD_WIDTH}×${HERO_CARD_HEIGHT}. Kaydetmeden önce kırpın.`,
                      })}
                      cropTitle={translate('heros.featured_crop_title', {
                        _: `Görseli Kırp (${HERO_CARD_WIDTH}×${HERO_CARD_HEIGHT})`,
                      })}
                      cropDesc={translate('heros.featured_crop_desc', {
                        _: 'Kırpma alanı hero kart oranında kilitli. Alanı sürükleyip boyutunu ayarlayabilirsiniz.',
                      })}
                    />
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}

        <Button
          type="button"
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAdd}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {translate('heros.add_event', { _: 'Etkinlik Ekle' })}
        </Button>
      </Box>
    </Box>
  );
};

export default HeroSpecialEvents;
